import { useEffect, useRef } from "react"
import { DiffEditor } from "@monaco-editor/react"
import type { editor } from "monaco-editor"
import { useEditorSettings } from "@/v2-backoffice-client/components/editor/editor.settings"

// Font size and line height come from the operator's editor settings.
const DIFF_EDITOR_OPTIONS: editor.IDiffEditorConstructionOptions = {
  readOnly: true,
  renderSideBySide: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
}

export type JsonDiffEditorProps = {
  original: string
  modified: string
}

/**
 * Read-only side-by-side JSON diff, shared by the history tab and the AI
 * assistant. Exists mainly to own the unmount sequence: @monaco-editor/react
 * disposes the text models BEFORE the diff widget, tripping Monaco's
 * "TextModel got disposed before DiffEditorWidget model got reset" assertion
 * every time a diff was on screen while switching tabs. keepCurrent*Model
 * stops the library's disposal; this component detaches the models first and
 * then disposes them itself, which is assertion-safe in either cleanup order.
 */
function JsonDiffEditor(props: JsonDiffEditorProps) {
  const { original, modified } = props

  const diffEditorRef = useRef<editor.IStandaloneDiffEditor | null>(null)
  const modelRef = useRef<editor.IDiffEditorModel | null>(null)
  const { monacoTheme, fontSize, lineHeight } = useEditorSettings()

  useEffect(() => {
    return () => {
      try {
        diffEditorRef.current?.setModel(null)
      } catch {
        // The library's own cleanup already disposed the widget — the models
        // below are detached either way.
      }
      modelRef.current?.original.dispose()
      modelRef.current?.modified.dispose()
      modelRef.current = null
    }
  }, [])

  return (
    <DiffEditor
      original={original}
      modified={modified}
      language="json"
      theme={monacoTheme}
      keepCurrentOriginalModel
      keepCurrentModifiedModel
      options={{ ...DIFF_EDITOR_OPTIONS, fontSize, lineHeight }}
      onMount={(diffEditor) => {
        diffEditorRef.current = diffEditor
        modelRef.current = diffEditor.getModel()
      }}
    />
  )
}

export default JsonDiffEditor
